import React, { useState, useEffect } from 'react';
import { DialogContent, DialogTitle, Dialog } from '@/components/ui/dialog';
import { buttonVariants, Button } from 'fluent-components';
import { X } from 'lucide-react';
import styles from '../../pages/DoctorManagementPage/components/style.module.css';

interface ButtonConfig {
  label: string;
  handleClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant: string;
}

interface SecondaryButtonConfig {
  handleClose: (e: React.MouseEvent<HTMLButtonElement>) => void; // or another element type
  label: string;
}

interface ConfirmationPopupProps {
  primaryButtonConfig: ButtonConfig;
  secondaryButtonConfig?: SecondaryButtonConfig;
  title: string;
  description?: string;
  open: boolean;
  onClose: () => void;
  onActionComplete: () => void;
}

const ConfirmationPopup: React.FC<ConfirmationPopupProps> = ({
  title,
  description,
  primaryButtonConfig,
  secondaryButtonConfig,
  open,
  onClose,
  onActionComplete,
}) => {
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    if (open) {
      setIsDisabled(false);
    }
  }, [open]);

  const handlePrimaryClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setIsDisabled(true); 
    primaryButtonConfig.handleClick(e);
    onActionComplete();
  };

  const handleSecondaryClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setIsDisabled(true); 
    secondaryButtonConfig?.handleClose(e);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogTitle className="text-xlg font-semibold font-sans">
          {title}
        </DialogTitle>
        {description && (
          <p className="text-[15px] leading-normal text-gray-600">
            {description}
          </p>
        )}
        <div className="flex justify-end gap-4 mt-2">
          {secondaryButtonConfig && (
            <Button
              variant="outline"
              onClick={handleSecondaryClick}
              className={styles['manage-booking-dropdown-outline']}
              disabled={isDisabled} 
            >
              {secondaryButtonConfig.label}
            </Button>
          )}
          <Button
            variant={primaryButtonConfig.variant as typeof buttonVariants}
            onClick={handlePrimaryClick}
            disabled={isDisabled} 
          >
            {primaryButtonConfig.label}
          </Button>
        </div>
        <button
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          onClick={onClose}
          disabled={isDisabled} 
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationPopup;
